/**
 * CountryFlag — the ONE way to show a country flag on the web (F32).
 *
 * Renders an `<img>` pointing at a vendored SVG. It replaces a text glyph: the
 * platform used to print a pair of Unicode regional-indicator characters, which
 * Windows' emoji font has no glyphs for, so about half of desktop traffic saw
 * two capital letters or an empty box wherever a flag belonged.
 *
 * ── Unknown countries (H12) ──────────────────────────────────────────────────
 *
 * An unrecognised code renders a DASHED PLACEHOLDER BOX, never a glyph. This is
 * the half of the defect that was not about Windows at all. The old helper
 * answered an unknown code with `'🏳️'`, a white flag — which renders correctly
 * on every platform, so "we do not know this country" was indistinguishable
 * from a country whose flag happens to be white. The bug was on macOS and iOS
 * too; it simply looked like an answer there. The dashed box is borrowed from
 * business-web's `RouteFlags`, the one component that already got this right.
 *
 * ── Accessibility ────────────────────────────────────────────────────────────
 *
 * Decorative by default: `alt=""` and `aria-hidden`, because a flag almost
 * always sits beside its own country name and "Portugal flag Portugal" is worse
 * than silence. Pass `label` where the flag is the ONLY identifier (a bare
 * language switcher, a route pill); it becomes the `alt` text. `@tonnie/ui`
 * holds no copy and calls no `t()`, so the caller supplies the translated
 * string.
 */

import * as React from 'react';

import { getFlagAssetUrl } from '@tonnie/assets';

import { cn } from '../lib/utils';

export type CountryFlagSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 * Flags are 3:2. Heights are fixed and widths derived, so a row of flags lines
 * up regardless of which country is in it — the emoji version wobbled because
 * each glyph carried its own font metrics.
 */
const SIZE_CLASSES: Record<CountryFlagSize, string> = {
  xs: 'h-3 w-[18px]',
  sm: 'h-3.5 w-[21px]',
  md: 'h-4 w-6',
  lg: 'h-6 w-9',
  xl: 'h-8 w-12',
};

const PLACEHOLDER_TEXT_CLASSES: Record<CountryFlagSize, string> = {
  xs: 'text-2xs',
  sm: 'text-2xs',
  md: 'text-2xs',
  lg: 'text-xs',
  xl: 'text-sm',
};

export interface CountryFlagProps {
  /** ISO-3166-1 alpha-2 code. Anything unrecognised renders the placeholder. */
  code: string | null | undefined;
  size?: CountryFlagSize;
  /**
   * Accessible name. Omit where a country name is already adjacent (the common
   * case) — the flag is then decorative. Supply the TRANSLATED string; this
   * package never calls `t()`.
   */
  label?: string;
  className?: string;
}

export function CountryFlag({ code, size = 'md', label, className }: CountryFlagProps) {
  const src = getFlagAssetUrl(code);

  if (src === null) {
    // Visibly not a flag. `data-country-flag="unknown"` is what tests assert
    // on — never a CSS class: `@tonnie/ui` primitives carry variant classes
    // unconditionally, so a class assertion is true whatever the component did.
    return (
      <span
        data-testid="country-flag"
        data-country-flag="unknown"
        data-country-code={code ? String(code).trim().toUpperCase() : ''}
        role={label ? 'img' : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
        title={label}
        className={cn(
          'inline-flex shrink-0 items-center justify-center rounded-[2px] border border-dashed border-muted-foreground/40 font-semibold leading-none text-muted-foreground',
          SIZE_CLASSES[size],
          PLACEHOLDER_TEXT_CLASSES[size],
          className
        )}
      >
        ?
      </span>
    );
  }

  return (
    <img
      data-testid="country-flag"
      data-country-flag="image"
      data-country-code={String(code).trim().toUpperCase()}
      src={src}
      alt={label ?? ''}
      aria-hidden={label ? undefined : true}
      title={label}
      loading="lazy"
      decoding="async"
      // `object-cover` + a fixed box: a couple of the vendored SVGs are not
      // exactly 3:2, and letterboxing them would put a stray background stripe
      // inside the flag.
      className={cn(
        'inline-block shrink-0 rounded-[2px] object-cover',
        SIZE_CLASSES[size],
        className
      )}
    />
  );
}

export interface CountryFlagPairProps {
  originCode: string | null | undefined;
  destinationCode: string | null | undefined;
  size?: CountryFlagSize;
  /** Translated accessible name for the whole route, e.g. "Brazil to Portugal". */
  routeLabel?: string;
  /** Separator between the two flags. Defaults to an arrow character. */
  separator?: React.ReactNode;
  className?: string;
}

/**
 * Origin → destination, the shape ~20 of the replaced sites were building by
 * hand out of two glyphs and an arrow character inside a template string.
 *
 * One `sr-only` label for the pair, both flags decorative — otherwise a screen
 * reader reads the route twice.
 */
export function CountryFlagPair({
  originCode,
  destinationCode,
  size = 'md',
  routeLabel,
  separator = '→',
  className,
}: CountryFlagPairProps) {
  return (
    <span
      data-testid="country-flag-pair"
      className={cn('inline-flex items-center gap-1.5', className)}
    >
      <CountryFlag code={originCode} size={size} />
      <span aria-hidden="true" className="text-muted-foreground">
        {separator}
      </span>
      <CountryFlag code={destinationCode} size={size} />
      {routeLabel ? <span className="sr-only">{routeLabel}</span> : null}
    </span>
  );
}
